import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    baseCreditCost: '',
    creditsPerDay: 10,
    minDays: 1,
    maxDays: 365,
    keyTag: '',
    productType: 'software',
    isActive: true,
    features: '',
    maxActivations: 1
  });

  const { success, error } = useNotification();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/products');
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      baseCreditCost: '',
      creditsPerDay: 10,
      minDays: 1,
      maxDays: 365,
      keyTag: '',
      productType: 'software',
      isActive: true,
      features: '',
      maxActivations: 1
    });
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        baseCreditCost: Math.max(0, parseInt(formData.baseCreditCost) || 0),
        creditsPerDay: Math.max(0, parseInt(formData.creditsPerDay) || 0),
        minDays: Math.max(1, parseInt(formData.minDays) || 1),
        maxDays: Math.max(1, parseInt(formData.maxDays) || 365),
        maxActivations: Math.max(1, parseInt(formData.maxActivations) || 1),
        features: formData.features.split(',').map(f => f.trim()).filter(f => f),
        stats: {
          totalGenerated: 0,
          totalSold: 0,
          revenue: 0
        }
      };

      const response = await axios.post('/api/products', productData);
      
      // Ensure the returned product has valid stats
      const newProduct = {
        ...response.data,
        stats: response.data.stats || { totalGenerated: 0, totalSold: 0, revenue: 0 }
      };
      
      setProducts([newProduct, ...products]);
      setShowCreateModal(false);
      resetForm();
      success('Product created successfully!');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to create product');
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    try {
      // Preserve existing stats
      const existingStats = selectedProduct?.stats || { 
        totalGenerated: 0, 
        totalSold: 0, 
        revenue: 0 
      };
      
      const productData = {
        ...formData,
        baseCreditCost: Math.max(0, parseInt(formData.baseCreditCost) || 0),
        creditsPerDay: Math.max(0, parseInt(formData.creditsPerDay) || 0),
        minDays: Math.max(1, parseInt(formData.minDays) || 1),
        maxDays: Math.max(1, parseInt(formData.maxDays) || 365),
        maxActivations: Math.max(1, parseInt(formData.maxActivations) || 1),
        features: formData.features.split(',').map(f => f.trim()).filter(f => f),
        stats: existingStats // Preserve existing stats
      };

      const response = await axios.put(`/api/products/${selectedProduct._id}`, productData);
      
      // Ensure the returned product has valid stats by falling back to original stats if needed
      const updatedProduct = {
        ...response.data,
        stats: response.data.stats || existingStats
      };
      
      setProducts(products.map(p => p._id === selectedProduct._id ? updatedProduct : p));
      setShowEditModal(false);
      setSelectedProduct(null);
      resetForm();
      success('Product updated successfully!');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to update product');
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await axios.delete(`/api/products/${selectedProduct._id}`);
      setProducts(products.filter(p => p._id !== selectedProduct._id));
      setShowDeleteModal(false);
      setSelectedProduct(null);
      success('Product deleted successfully!');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      baseCreditCost: product.baseCreditCost,
      creditsPerDay: product.creditsPerDay,
      minDays: product.minDays,
      maxDays: product.maxDays,
      keyTag: product.keyTag,
      productType: product.productType,
      isActive: product.isActive,
      features: product.features?.join(', ') || '',
      maxActivations: product.maxActivations || 1
    });
    setShowEditModal(true);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'software':
        return 'text-blue-400 bg-blue-600/20';
      case 'game':
        return 'text-green-400 bg-green-600/20';
      case 'service':
        return 'text-purple-400 bg-purple-600/20';
      default:
        return 'text-gray-400 bg-gray-600/20';
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.keyTag.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || product.productType === typeFilter;
    const matchesStatus = statusFilter === '' || 
                         (statusFilter === 'active' && product.isActive) ||
                         (statusFilter === 'inactive' && !product.isActive);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Check if date is valid and not in the future
    if (isNaN(date.getTime()) || date > new Date()) {
      return 'N/A';
    }
    
    return date.toLocaleDateString();
  };

  const safeStat = (value, defaultValue = 0) => {
    const num = Number(value);
    return !isNaN(num) && num >= 0 ? num : defaultValue;
  };

  const ProductCard = ({ product }) => (
    <div className="card-astral p-6 hover:glow-purple transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-white">{product.name}</h3>
            <span className={`px-2 py-1 text-xs rounded-full capitalize ${getTypeColor(product.productType)}`}>
              {product.productType}
            </span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
              {product.keyTag}
            </span>
            <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">
              {product.minDays}-{product.maxDays} days
            </span>
            <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">
              Max: {product.maxActivations || 1}
            </span>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${
          product.isActive ? 'text-green-400 bg-green-600/20' : 'text-red-400 bg-red-600/20'
        }`}>
          {product.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
      
      <p className="text-gray-400 mb-4 line-clamp-2">{product.description}</p>
      
      {product.features && product.features.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Features:</h4>
          <div className="flex flex-wrap gap-1">
            {product.features.slice(0, 3).map((feature, index) => (
              <span key={index} className="px-2 py-1 bg-indigo-600/20 text-indigo-400 text-xs rounded">
                {feature}
              </span>
            ))}
            {product.features.length > 3 && (
              <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">
                +{product.features.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-blue-600/10 rounded-lg">
          <div className="text-blue-400 font-semibold">{safeStat(product.baseCreditCost)}</div>
          <div className="text-xs text-gray-400">Base Cost</div>
        </div>
        <div className="text-center p-3 bg-green-600/10 rounded-lg">
          <div className="text-green-400 font-semibold">{safeStat(product.creditsPerDay)}</div>
          <div className="text-xs text-gray-400">Per Day</div>
        </div>
      </div>

      <div className="bg-dark-200 p-3 rounded-lg mb-4">
        <div className="text-xs text-gray-400 mb-1">Sample costs:</div>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-400">1 day:</span>
            <span className="text-white">{product.baseCreditCost + product.creditsPerDay} credits</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">7 days:</span>
            <span className="text-white">{product.baseCreditCost + (7 * product.creditsPerDay)} credits</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">30 days:</span>
            <span className="text-white">{product.baseCreditCost + (30 * product.creditsPerDay)} credits</span>
          </div>
        </div>
      </div>

      {product.stats && (
        <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
          <div className="text-center">
            <div className="text-white font-semibold">{safeStat(product.stats.totalGenerated)}</div>
            <div className="text-gray-500">Generated</div>
          </div>
          <div className="text-center">
            <div className="text-white font-semibold">{safeStat(product.stats.totalSold)}</div>
            <div className="text-gray-500">Sold</div>
          </div>
          <div className="text-center">
            <div className="text-white font-semibold">${safeStat(product.stats.revenue, 0).toFixed(2)}</div>
            <div className="text-gray-500">Revenue</div>
          </div>
        </div>
      )}
      
      <div className="text-right text-xs text-gray-500 mb-4">
        Created: {formatDate(product.createdAt)}
      </div>

      <div className="flex space-x-2">
        <button
          onClick={() => openEditModal(product)}
          className="flex-1 btn-neon-blue text-xs py-2"
        >
          Edit
        </button>
        <button
          onClick={() => {
            setSelectedProduct(product);
            setShowDeleteModal(true);
          }}
          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs py-2 px-3 rounded transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <h2 className="text-2xl font-bold text-white">Product Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="btn-neon"
        >
          Create New Product
        </button>
      </div>

      {/* Filters */}
      <div className="card-astral p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Search Products
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or tag..."
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            >
              <option value="">All Types</option>
              <option value="software">Software</option>
              <option value="game">Game</option>
              <option value="service">Service</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('');
                setStatusFilter('');
              }}
              className="btn-neon-blue w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="card-astral p-8 text-center">
          <h3 className="text-xl font-bold text-gray-400 mb-2">No Products Found</h3>
          <p className="text-gray-500">
            {products.length === 0 
              ? 'Create your first product to get started!' 
              : 'No products match your current filters.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      )}

      {/* Create/Edit Product Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">
              {showCreateModal ? 'Create New Product' : 'Edit Product'}
            </h2>
            <form onSubmit={showCreateModal ? handleCreateProduct : handleEditProduct} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Key Tag (2-10 chars) *
                  </label>
                  <input
                    type="text"
                    value={formData.keyTag}
                    onChange={(e) => setFormData({...formData, keyTag: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white font-mono"
                    placeholder="e.g., SOFT1"
                    pattern="[A-Z0-9]{2,10}"
                    maxLength={10}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  rows="3"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Product Type
                  </label>
                  <select
                    value={formData.productType}
                    onChange={(e) => setFormData({...formData, productType: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  >
                    <option value="software">Software</option>
                    <option value="game">Game</option>
                    <option value="service">Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Minimum Days
                  </label>
                  <input
                    type="number"
                    value={formData.minDays}
                    onChange={(e) => setFormData({...formData, minDays: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Maximum Days
                  </label>
                  <input
                    type="number"
                    value={formData.maxDays}
                    onChange={(e) => setFormData({...formData, maxDays: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Base Credit Cost *
                  </label>
                  <input
                    type="number"
                    value={formData.baseCreditCost}
                    onChange={(e) => setFormData({...formData, baseCreditCost: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                    required
                    min="1"
                    placeholder="e.g., 5"
                  />
                  <p className="text-xs text-gray-500 mt-1">One-time base cost</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Credits Per Day *
                  </label>
                  <input
                    type="number"
                    value={formData.creditsPerDay}
                    onChange={(e) => setFormData({...formData, creditsPerDay: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                    required
                    min="1"
                    placeholder="e.g., 10"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cost per day of license</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Features (comma separated)
                </label>
                <input
                  type="text"
                  value={formData.features}
                  onChange={(e) => setFormData({...formData, features: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  placeholder="e.g., Advanced Features, Multi-user, Cloud Sync"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separate features with commas. These will be shown as tags.
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm text-gray-300">
                  Product Active (visible to users)
                </label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-neon flex-1">
                  {showCreateModal ? 'Create Product' : 'Update Product'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (showCreateModal) setShowCreateModal(false);
                    if (showEditModal) setShowEditModal(false);
                    setSelectedProduct(null);
                    resetForm();
                  }}
                  className="btn-neon-blue flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Delete Product</h2>
            <p className="text-gray-400 mb-4">
              Are you sure you want to delete "{selectedProduct?.name}"? This action cannot be undone.
            </p>
            <div className="bg-red-600/20 border border-red-500/50 rounded-lg p-3 mb-4">
              <p className="text-red-400 text-sm">
                ⚠️ Warning: All associated license keys will also be affected.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteProduct}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded transition-colors"
              >
                Delete Product
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedProduct(null);
                }}
                className="btn-neon-blue flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
